import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Archive, ArrowLeft, Mail, MailOpen, Plus } from "lucide-react";
import {
    getLocationsUsed,
    getApartmentsByLocation,
    createConversation,
    sendMessage,
    sendMessageWithFile,
    getConversationMessages,
    getToMeConversations,
    getArchivedConversations,
    archiveConversation,
} from "../services/chat";
import { downloadAttachment, validateFileType } from "../utils/chat-download";
import { apiFetch } from "../utils/apiFetch";
import { fetchLocations } from "../services/locations";
import { useWebSocket } from "../context/WebSocketContext"
import { API_BASE_OPERATOR as API_BASE, CHAT_ARCHIVE_DAYS } from "../config/vars";
import { notifyInAppSound, playNotificationSound } from "../utils/notificationSound";
import FilterPopover, { DateRangeFields } from "../components/FilterPopover";
import ToolbarSearch from "../components/ToolbarSearch";
import "../styles/chat.css";

const readPendingOperatorChatOpen = () => {
    try {
        const rawHeadId = localStorage.getItem("pendingChatOpenHeadId_operator");
        const headId = parseInt(rawHeadId, 10);
        if (!Number.isFinite(headId) || headId <= 0) {
            return null;
        }

        return {
            headId,
            title: (localStorage.getItem("pendingChatOpenTitle_operator") || "").trim(),
        };
    } catch (_) {
        return null;
    }
};

const ChatOperators = ({ onUnreadChatsCountChange, onShellNavChange }) => {
    const initialPendingChatOpenRef = useRef(readPendingOperatorChatOpen());
    const [viewMode, setViewMode] = useState(() => (initialPendingChatOpenRef.current ? "chat" : "active")); // active | archived | new
    const [listMode, setListMode] = useState("active");
    const [createMode, setCreateMode] = useState(""); // client | location | network
    const [showArchiveWarning, setShowArchiveWarning] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [showUnreadOnly, setShowUnreadOnly] = useState(false);
    const [archivedFrom, setArchivedFrom] = useState("");
    const [archivedTo, setArchivedTo] = useState("");

    const [residentLocations, setResidentLocations] = useState([]);
    const [apartments, setApartments] = useState([]);
    const [locations, setLocations] = useState([]);

    const [selectedResidentLocationId, setSelectedResidentLocationId] = useState("");
    const [selectedApartment, setSelectedApartment] = useState("");
    const [selectedLocation, setSelectedLocation] = useState("");

    const [activeConversation, setActiveConversation] = useState(() => {
        const pending = initialPendingChatOpenRef.current;
        if (!pending) {
            return null;
        }

        return {
            id: pending.headId,
            title: pending.title || "Переписка",
            work_mode: "network",
            status: "opened",
            updated_at: new Date().toISOString(),
            last_message: null,
            unread_count: 0,
            can_archive: true,
        };
    });
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [file, setFile] = useState(null);
    const [showAttachmentInfo, setShowAttachmentInfo] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [conversationsList, setConversationsList] = useState([]);
    const [activeListLoaded, setActiveListLoaded] = useState(false);
    const [title, setTitle] = useState(() => initialPendingChatOpenRef.current?.title || "");
    const [showCreateDropdown, setShowCreateDropdown] = useState(false);
    const [highlightedChats, setHighlightedChats] = useState(new Set());

    const fileInputRef = useRef(null);
    const dropdownRef = useRef(null);
    const textareaRef = useRef(null);
    const activeConversationRef = useRef(null);
    const chatWindowRef = useRef(null);
    const chatMessagesRef = useRef(null);
    const chatListScrollRef = useRef(null);

    const [showScrollToTop, setShowScrollToTop] = useState(false);
    const [showScrollToBottom, setShowScrollToBottom] = useState(false);

    const operatorId = parseInt(localStorage.getItem("operatorId"));
    const networkId = parseInt(localStorage.getItem("networkId"));
    const activeConversationId = activeConversation?.id ?? null;
    const activeConversationListEntry = activeConversationId
        ? conversationsList.find((conversation) => conversation.id === activeConversationId)
        : null;
    const activeConversationTitle =
        activeConversationListEntry?.title ||
        activeConversation?.title ||
        title ||
        "Переписка";
    const canComposeInActiveConversation = Boolean(
        activeConversation && ((activeConversationListEntry?.status || activeConversation?.status) === "opened")
    );
    const { connect, subscribe, unsubscribe } = useWebSocket();

    const CHAT_TZ = "Europe/Kyiv";

    const [attachmentObjectUrls, setAttachmentObjectUrls] = useState({}); // filename -> objectURL
    const attachmentObjectUrlsRef = useRef(new Map());

    const isHttpUrl = useCallback((value) => /^https?:\/\//i.test(String(value || "")), []);

    const getAttachmentFilename = useCallback((fileUrl) => {
        if (!fileUrl) return "";
        const raw = String(fileUrl).trim();
        if (!raw) return "";
        const last = raw.split("/").pop();
        return (last || "").trim();
    }, []);

    const ensureAttachmentObjectUrl = useCallback(
        async (filename) => {
            const key = String(filename || "").trim();
            if (!key || !API_BASE) return;
            if (attachmentObjectUrlsRef.current.has(key)) return;

            try {
                const res = await apiFetch(`${API_BASE}/chat/operator/download/${encodeURIComponent(key)}`);
                if (!res.ok) return;
                const blob = await res.blob();
                const objectUrl = window.URL.createObjectURL(blob);
                attachmentObjectUrlsRef.current.set(key, objectUrl);
                setAttachmentObjectUrls((prev) => ({ ...prev, [key]: objectUrl }));
            } catch (_) {
                // ignore
            }
        },
        []
    );

    useEffect(() => {
        const urlMap = attachmentObjectUrlsRef.current;
        return () => {
            for (const url of urlMap.values()) {
                try {
                    window.URL.revokeObjectURL(url);
                } catch (_) {
                    // ignore
                }
            }
            urlMap.clear();
        };
    }, []);

    useEffect(() => {
        const toFetch = new Set();
        for (const msg of messages || []) {
            for (const att of msg?.attachments || []) {
                if (att?.file_type && String(att.file_type).startsWith("image/")) {
                    const fn = getAttachmentFilename(att?.file_url);
                    if (fn && !attachmentObjectUrlsRef.current.has(fn)) toFetch.add(fn);
                }
            }
        }
        for (const fn of toFetch) {
            ensureAttachmentObjectUrl(fn);
        }
    }, [messages, getAttachmentFilename, ensureAttachmentObjectUrl]);

    // Backend may return timestamps without timezone info (e.g. UTC time but no trailing 'Z').
    // JS treats such strings as local time, which can produce a consistent -2h offset for Kyiv.
    // Normalize: if timezone is missing, treat as UTC.
    const parseApiDate = useCallback((value) => {
        if (!value) return null;
        if (value instanceof Date) return value;

        if (typeof value === "number") {
            const d = new Date(value);
            return Number.isNaN(d.getTime()) ? null : d;
        }

        if (typeof value !== "string") return null;

        let s = value.trim();
        if (!s) return null;

        // Convert "YYYY-MM-DD HH:MM:SS" -> "YYYY-MM-DDTHH:MM:SS"
        s = s.replace(
            /^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2}:\d{2}(?:\.\d+)?)(.*)$/,
            "$1T$2$3"
        );

        // Trim fractional seconds to milliseconds (JS Date is not reliable with >3 digits)
        s = s.replace(/\.(\d{3})\d+(?=(Z|[+-]\d{2}:?\d{2})?$)/, ".$1");

        const hasTz = /(Z|[+-]\d{2}:?\d{2})$/i.test(s);
        if (!hasTz) s = `${s}Z`;

        const d = new Date(s);
        return Number.isNaN(d.getTime()) ? null : d;
    }, []);

    const formatChatDateTime = useCallback((value) => {
        if (!value) return "";
        const d = parseApiDate(value);
        if (!d) return "";
        return d.toLocaleString("uk-UA", {
            timeZone: CHAT_TZ,
            day: "2-digit",
            month: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
    }, [CHAT_TZ, parseApiDate]);

    const formatChatTime = useCallback((value) => {
        if (!value) return "";
        const d = parseApiDate(value);
        if (!d) return "";
        return d.toLocaleTimeString("uk-UA", {
            timeZone: CHAT_TZ,
            hour: "2-digit",
            minute: "2-digit",
        });
    }, [CHAT_TZ, parseApiDate]);

    const formatYmdForFilter = useCallback((value) => {
        if (!value) return "";
        const d = parseApiDate(value);
        if (!d) return "";
        // en-CA gives YYYY-MM-DD
        return new Intl.DateTimeFormat("en-CA", {
            timeZone: CHAT_TZ,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        }).format(d);
    }, [CHAT_TZ, parseApiDate]);

    const getChatScrollEl = useCallback(() => chatMessagesRef.current, []);

    const updateScrollNav = useCallback(() => {
        const el = getChatScrollEl();
        if (!el) return;

        const overflow = el.scrollHeight - el.clientHeight;
        const isLong = overflow > 400;
        if (!isLong) {
            setShowScrollToTop(false);
            setShowScrollToBottom(false);
            return;
        }

        const threshold = 160;
        const top = el.scrollTop;
        const bottomGap = el.scrollHeight - (el.scrollTop + el.clientHeight);

        setShowScrollToTop(top > threshold);
        setShowScrollToBottom(bottomGap > threshold);
    }, [getChatScrollEl]);

    const scrollToTop = useCallback(() => {
        const el = getChatScrollEl();
        if (!el) return;
        el.scrollTo({ top: 0, behavior: "smooth" });
    }, [getChatScrollEl]);

    const scrollToBottom = useCallback(() => {
        const el = getChatScrollEl();
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    }, [getChatScrollEl]);

    const handleChatListWheel = useCallback((event) => {
        const container = chatListScrollRef.current;
        if (!container) return;

        const { deltaY } = event;
        const canScroll = container.scrollHeight > container.clientHeight;
        if (!canScroll || deltaY === 0) return;

        const atTop = container.scrollTop <= 0;
        const atBottom = Math.ceil(container.scrollTop + container.clientHeight) >= container.scrollHeight;
        const scrollingUp = deltaY < 0;
        const scrollingDown = deltaY > 0;

        if ((scrollingUp && atTop) || (scrollingDown && atBottom)) {
            return;
        }

        event.preventDefault();
        container.scrollTop += deltaY;
    }, []);

    useEffect(() => {
        if (viewMode !== "chat" || !activeConversation) return;

        const el = getChatScrollEl();
        if (!el) return;

        const handler = () => updateScrollNav();
        handler();

        el.addEventListener("scroll", handler, { passive: true });
        window.addEventListener("resize", handler);

        return () => {
            el.removeEventListener("scroll", handler);
            window.removeEventListener("resize", handler);
        };
    }, [viewMode, activeConversation, messages.length, getChatScrollEl, updateScrollNav]);

    useEffect(() => {
        activeConversationRef.current = activeConversation;
    }, [activeConversation]);

    useEffect(() => {
        if (viewMode !== "chat" || !activeConversationId || !textareaRef.current) {
            return;
        }

        const frameId = window.requestAnimationFrame(() => {
            textareaRef.current?.focus();
        });

        return () => window.cancelAnimationFrame(frameId);
    }, [viewMode, activeConversationId]);

    // ===== Автоматичне розширення textarea (тільки мобільне) =====
    const handleTextareaChange = (e) => {
        setText(e.target.value);
        resizeTextareaMobile();
    };

    const resizeTextareaMobile = () => {
        if (textareaRef.current && window.innerWidth <= 768) {
            // Тільки на мобільному
            textareaRef.current.style.height = "auto";
            const scrollHeight = textareaRef.current.scrollHeight;
            // Максимум до 4 рядків (~100px на мобільному)
            textareaRef.current.style.height = Math.min(scrollHeight, 100) + "px";
        }
    };

    useEffect(() => {
        resizeTextareaMobile();
    }, [text]);

    useEffect(() => {
        if (!activeConversation)
            return;

        const roomId = activeConversation.id;
        const handleNewMessage = (msg) => {
            // Don't append our own messages twice; account for possible ID collisions across sender types
            if (!(msg.sender_mode === "operator" && Number(msg.sender_id) === operatorId)) {
                playNotificationSound();
                setMessages((prev) => [...prev, msg]);
            }
        };

        // Messages are delivered via role-specific rooms to avoid leaking sensitive sender info.
        connect("chat_operator", roomId);
        subscribe("chat_operator", roomId, "new_message", handleNewMessage);

        return () => {
            unsubscribe("chat_operator", roomId, "new_message", handleNewMessage);
        };
    }, [connect, subscribe, unsubscribe, activeConversation, operatorId]);

    const loadMessagesByHeadId = useCallback(async (headId) => {
        if (!headId) return;
        try {
            const msgs = await getConversationMessages(headId);
            setMessages(msgs);
        } catch (err) {
            console.error(err);
            setError("Не вдалося отримати повідомлення");
        }
    }, []);

    // Auto-open a conversation requested by another page (e.g. Cars -> create chat)
    useEffect(() => {
        const raw = localStorage.getItem("pendingChatOpenHeadId_operator");
        if (!raw) return;

        const headId = parseInt(raw);
        if (!Number.isFinite(headId) || headId <= 0) {
            localStorage.removeItem("pendingChatOpenHeadId_operator");
            localStorage.removeItem("pendingChatOpenTitle_operator");
            return;
        }

        const titleFromStorage = (localStorage.getItem("pendingChatOpenTitle_operator") || "").trim();
        const found = conversationsList.find((c) => Number(c?.id) === headId);
        const currentConversation = activeConversationRef.current;
        const alreadyOpen = currentConversation && Number(currentConversation.id) === headId ? currentConversation : null;
        const conv = found || alreadyOpen || {
            id: headId,
            title: titleFromStorage || "Переписка",
            work_mode: "network",
            status: "opened",
            updated_at: new Date().toISOString(),
            last_message: null,
            unread_count: 0,
            can_archive: true,
        };

        activeConversationRef.current = conv;
        setActiveConversation(conv);
        setTitle(conv.title || titleFromStorage);
        setViewMode("chat");
        setHighlightedChats((prev) => {
            const next = new Set(prev);
            next.delete(headId);
            return next;
        });
        loadMessagesByHeadId(headId);
        setConversationsList((prev) => {
            const exists = (prev || []).some((c) => Number(c?.id) === headId);
            if (exists) {
                return (prev || []).map((c) => (Number(c?.id) === headId ? { ...c, unread_count: 0 } : c));
            }
            return [conv, ...(prev || [])];
        });

        localStorage.removeItem("pendingChatOpenHeadId_operator");
        localStorage.removeItem("pendingChatOpenTitle_operator");

    }, [conversationsList, activeListLoaded, loadMessagesByHeadId]);

    // Subscribe to chat updates for this operator
    useEffect(() => {
        if (!operatorId)
            return;

        const roomId = `operator:${operatorId}`;
        const handleChatHighlighted = (payload) => {
            if (payload.head_id) {
                setHighlightedChats(prev => new Set([...prev, payload.head_id]));
                // Remove highlight after 60 seconds
                setTimeout(() => {
                    setHighlightedChats(prev => {
                        const updated = new Set(prev);
                        updated.delete(payload.head_id);
                        return updated;
                    });
                }, 60000);
            }
        };

        connect("chat", roomId);

        subscribe("chat", roomId, "chat_highlighted", handleChatHighlighted);

        return () => {
            unsubscribe("chat", roomId, "chat_highlighted", handleChatHighlighted);
        };
    }, [connect, subscribe, unsubscribe, operatorId]);

    // Закриття dropdown при кліку поза
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowCreateDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // ===== Завантаження активних переписок =====
    const loadActiveConversations = useCallback(async (options = { setMode: true }) => {
        try {
            const hasPendingOpen = Boolean(localStorage.getItem("pendingChatOpenHeadId_operator"));
            if (options?.setMode && !activeConversationRef.current && !hasPendingOpen) {
                setViewMode("active");
                setArchivedFrom("");
                setArchivedTo("");
            }
            setListMode("active");
            const convs = await getToMeConversations();
            setConversationsList(convs);
            setActiveListLoaded(true);
        } catch (err) {
            console.error(err);
            setError("Не вдалося завантажити активні переписки");
        }
    }, []);

    // ===== Завантаження архівних переписок =====
    const loadArchivedConversations = useCallback(async () => {
        try {
            const convs = await getArchivedConversations();
            setConversationsList(convs);
            setListMode("archived");
            setViewMode("archived");
        } catch (err) {
            console.error(err);
            setError("Не вдалося завантажити архівні переписки");
        }
    }, []);

    const showActiveConversations = useCallback(() => {
        setShowCreateDropdown(false);
        setArchivedFrom("");
        setArchivedTo("");
        setListMode("active");
        setViewMode("active");
        loadActiveConversations({ setMode: false });
    }, [loadActiveConversations]);

    const showArchivedConversations = useCallback(() => {
        setShowCreateDropdown(false);
        setListMode("archived");
        setViewMode("archived");
        loadArchivedConversations();
    }, [loadArchivedConversations]);

    const handleListSectionChange = useCallback((nextMode) => {
        if (nextMode === "archived") {
            showArchivedConversations();
            return;
        }
        showActiveConversations();
    }, [showActiveConversations, showArchivedConversations]);

    // ===== Початкове завантаження =====
    useEffect(() => {
        loadActiveConversations();
    }, [loadActiveConversations]);

    // ===== Обработка новых чатов и повідомлень =====
    useEffect(() => {
        if (!operatorId) return;

        const roomId = `operator:${operatorId}`;
        const handleChatNew = (payload) => {
            notifyInAppSound();
            if (payload.id) {
                setHighlightedChats(prev => new Set([...prev, payload.id]));
                setTimeout(() => {
                    setHighlightedChats(prev => {
                        const updated = new Set(prev);
                        updated.delete(payload.id);
                        return updated;
                    });
                }, 60000);
            }
            // Refresh the list, but don't kick the user out of an open conversation.
            if (listMode === "archived") {
                loadArchivedConversations();
            } else {
                loadActiveConversations({ setMode: false });
            }
        };

        subscribe("chat", roomId, "chat_new", handleChatNew);

        return () => {
            unsubscribe("chat", roomId, "chat_new", handleChatNew);
        };
    }, [subscribe, unsubscribe, operatorId, listMode, loadActiveConversations, loadArchivedConversations]);

    // ===== Локації / квартири =====
    const loadResidentLocations = useCallback(async () => {
        try {
            const data = await getLocationsUsed();
            setResidentLocations((data?.locations || []).filter((l) => l && l.id));
        } catch (err) {
            console.error(err);
            setResidentLocations([]);
            setError(err?.message || "Не вдалося завантажити список локацій");
        }
    }, []);

    const loadApartments = useCallback(async (locationId) => {
        setApartments([]);
        if (!locationId) return;
        try {
            const data = await getApartmentsByLocation(locationId);
            setApartments(data.filter((a) => a.apartment));
        } catch (err) {
            console.error(err);
            setError(err?.message || "Не вдалося завантажити список квартир");
        }
    }, []);

    // ===== Завантаження локацій =====
    const loadLocations = useCallback(async () => {
        try {
            const data = await fetchLocations();
            setLocations(data);
        } catch (err) {
            console.error(err);
            setError("Не вдалося завантажити список локацій");
        }
    }, []);

    // ===== Залежності вибору =====
    useEffect(() => {
        if (selectedResidentLocationId) {
            loadApartments(selectedResidentLocationId);
            setSelectedApartment("");
        }
    }, [selectedResidentLocationId, loadApartments]);

    useEffect(() => {
        if (createMode === "client") loadResidentLocations();
        if (createMode === "location") loadLocations();
    }, [createMode, loadLocations, loadResidentLocations]);

    // ===== Початок створення нової переписки =====
    const startNewChat = (mode) => {
        setCreateMode(mode);
        setViewMode("new");
        setShowCreateDropdown(false);
        setError("");
        setTitle("");
        setSelectedResidentLocationId("");
        setSelectedApartment("");
        setSelectedLocation("");
    };

    const returnToCreatePicker = () => {
        setViewMode(listMode);
        setShowCreateDropdown(true);
        setCreateMode("");
        setTitle("");
        setError("");
    };

    const handleCreateOptionToggle = () => {
        setShowArchiveWarning(true);
        setTimeout(() => setShowArchiveWarning(false), 5000);
        setShowCreateDropdown((prev) => !prev);
    };

    // ===== Створення переписки =====
    const createNewConversation = async () => {
        if (!title.trim()) {
            setError("Введіть тему переписки");
            return;
        }

        if (createMode === "client" && (!selectedResidentLocationId || !selectedApartment)) {
            setError("Оберіть локацію та квартиру");
            return;
        }

        if (createMode === "location" && !selectedLocation) {
            setError("Оберіть локацію");
            return;
        }

        setLoading(true);
        try {
            const payload = {
                title,
                work_mode: createMode,
                network_id: networkId || null,
            };

            if (createMode === "client") {
                payload.apartment = selectedApartment;
                payload.location_id = parseInt(selectedResidentLocationId);
            }
            if (createMode === "location") payload.location_id = parseInt(selectedLocation);

            const conv = await createConversation(payload);
            setActiveConversation(conv);
            setTitle(title);
            setMessages([]);

            // Додати нову переписку в список щоб кнопка архівування працювала
            const newConv = {
                id: conv.head_id || conv.id,
                title: title,
                work_mode: createMode,
                status: "opened",
                updated_at: new Date().toISOString(),
                last_message: null,
                unread_count: 0,
                can_archive: true,
            };
            setConversationsList(prev => [newConv, ...prev]);

            // Оновити activeConversation з правильним id для пошуку
            setActiveConversation({
                ...conv,
                id: conv.head_id || conv.id
            });

            setViewMode("chat");
            setError("");
        } catch (err) {
            console.error(err);
            setError(err?.message || "Не вдалося створити переписку");
        } finally {
            setLoading(false);
        }
    };

    // ===== Повідомлення =====
    const loadMessages = useCallback(async (headId = activeConversationRef.current?.id || activeConversation?.id) => {
        await loadMessagesByHeadId(headId);
    }, [activeConversation?.id, loadMessagesByHeadId]);

    const handleSend = async () => {
        const convId = activeConversation?.id || activeConversation?.head_id;
        if ((!text.trim() && !file) || !convId) return;
        setLoading(true);
        try {
            let msg;
            if (file) {
                msg = await sendMessageWithFile(convId, text, file);
                setFile(null);
            } else {
                msg = await sendMessage({
                    head_id: convId,
                    text,
                });
            }
            setMessages((prev) => [...prev, msg]);
            setText("");
        } catch (err) {
            console.error(err);
            if (err.message && err.message.includes("архівована")) {
                setError(`⚠️ Переписка архівована. Повідомлення без активної переписки архівуються через ${CHAT_ARCHIVE_DAYS} днів.`);
            } else {
                setError(err?.message || "Не вдалося надіслати повідомлення");
            }
        } finally {
            setLoading(false);
        }
    };

    const openConversation = async (conv) => {
        setActiveConversation(conv);
        setTitle(conv.title);
        setViewMode("chat");

        // Clear unread locally so the list updates immediately for this operator.
        setConversationsList((prev) =>
            prev.map((c) => (c.id === conv.id ? { ...c, unread_count: 0 } : c))
        );

        // Видаляємо з highlighted (жовтий фон)
        setHighlightedChats(prev => {
            const updated = new Set(prev);
            updated.delete(conv.id);
            return updated;
        });
        await loadMessages(conv.id);
        setError("");
    };

    const handleArchive = async () => {
        try {
            await archiveConversation(activeConversation.id);
            setError("✅ Переписку переміщено в архів");
            setTimeout(() => setError(""), 3000);
            setActiveConversation(null);
            setViewMode("active");
            loadActiveConversations();
        } catch (err) {
            console.error(err);
            setError("❌ Не вдалося архівувати переписку");
        }
    };

    const backToList = () => {
        setActiveConversation(null);
        if (listMode === "archived") {
            setViewMode("archived");
            loadArchivedConversations();
            return;
        }
        showActiveConversations();
    };

    const displayedConversations = useMemo(() => {
        const sorted = [...conversationsList].sort((a, b) => {
            const tb = parseApiDate(b.updated_at)?.getTime() || 0;
            const ta = parseApiDate(a.updated_at)?.getTime() || 0;
            return tb - ta;
        });

        const query = searchQuery.trim().toLowerCase();

        const matchesQuickFilters = (conv) => {
            if (showUnreadOnly && Number(conv?.unread_count || 0) <= 0) {
                return false;
            }

            if (!query) return true;

            const haystack = [
                conv?.title,
                conv?.last_message?.text,
                conv?.work_mode === "client" ? "мешканець" : "",
                conv?.work_mode === "location" ? "локація" : "",
                conv?.work_mode === "network" ? "осбб" : "",
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();

            return haystack.includes(query);
        };

        if (viewMode !== "archived") {
            return sorted.filter(matchesQuickFilters);
        }

        if (!archivedFrom && !archivedTo) {
            return sorted.slice(0, 5).filter(matchesQuickFilters);
        }

        let from = archivedFrom;
        let to = archivedTo;
        if (from && to && from > to) {
            [from, to] = [to, from];
        }

        return sorted.filter((conv) => {
            const dateSource = conv.updated_at || conv.last_message?.created_at;
            if (!dateSource) return false;

            const convDate = formatYmdForFilter(dateSource);
            const fromOk = !from || convDate >= from;
            const toOk = !to || convDate <= to;
            return fromOk && toOk && matchesQuickFilters(conv);
        });
    }, [conversationsList, viewMode, archivedFrom, archivedTo, formatYmdForFilter, parseApiDate, searchQuery, showUnreadOnly]);

    useEffect(() => {
        if (typeof onUnreadChatsCountChange !== "function") return;
        const unreadCount = Array.isArray(conversationsList)
            ? conversationsList.filter((conversation) => Number(conversation?.unread_count) > 0).length
            : 0;
        onUnreadChatsCountChange(unreadCount);
    }, [conversationsList, onUnreadChatsCountChange]);

    useEffect(() => {
        if (typeof onShellNavChange !== "function") return undefined;

        if (viewMode === "chat" && activeConversation) {
            const canArchive = Boolean(conversationsList.find(
                (c) => c.id === activeConversation.id && c.status === "opened" && c.can_archive
            ));
            onShellNavChange({
                focused: true,
                title: activeConversationTitle,
                back: backToList,
                canArchive,
                onArchive: handleArchive,
            });
        } else if (viewMode === "new") {
            onShellNavChange({
                focused: true,
                title: "Нова переписка",
                back: backToList,
                canArchive: false,
                onArchive: null,
            });
        } else {
            onShellNavChange(null);
        }

        return () => onShellNavChange(null);
    }, [viewMode, activeConversation, activeConversationTitle, conversationsList, onShellNavChange]); // eslint-disable-line react-hooks/exhaustive-deps

    const shellNavActive = (viewMode === "chat" && activeConversation) || viewMode === "new";
    const chatFilterActive = Boolean(listMode === "archived" || archivedFrom || archivedTo);
    const canArchiveActiveConversation = Boolean(
        viewMode === "chat"
        && activeConversation
        && (activeConversationListEntry?.status || activeConversation?.status) === "opened"
        && (activeConversationListEntry?.can_archive ?? activeConversation?.can_archive)
    );

    return (
        <div className={`chat-container ${shellNavActive ? "chat-container--shell-nav" : ""}`}>
            {shellNavActive ? (
                <div className="config-header">
                    <div className="header-buttons">
                        <button
                            type="button"
                            className="add-btn toolbar-icon-btn"
                            onClick={backToList}
                            onPointerUp={(e) => e.currentTarget.blur()}
                            title="Назад"
                            aria-label="Назад до списку переписок"
                        >
                            <ArrowLeft size={16} />
                        </button>
                        {canArchiveActiveConversation ? (
                            <button
                                type="button"
                                className="add-btn toolbar-icon-btn"
                                onClick={handleArchive}
                                onPointerUp={(e) => e.currentTarget.blur()}
                                title="Архівувати"
                                aria-label="Архівувати переписку"
                            >
                                <Archive size={16} />
                            </button>
                        ) : null}
                    </div>
                </div>
            ) : null}

            {/* ======== Список переписок ======== */}
            {(viewMode === "active" || viewMode === "archived") && (
                <>
                    <div className="config-header">
                        <div className="header-buttons">
                            <button
                                type="button"
                                className={`add-btn toolbar-icon-btn ${showCreateDropdown ? "is-active" : ""}`}
                                onClick={(e) => {
                                    e.currentTarget.blur();
                                    handleCreateOptionToggle();
                                }}
                                onPointerUp={(e) => e.currentTarget.blur()}
                                title="Нова переписка"
                                aria-label="Нова переписка"
                                aria-expanded={showCreateDropdown}
                            >
                                <Plus size={16} />
                            </button>
                            <FilterPopover
                                active={chatFilterActive}
                                title="Фільтри переписок"
                                hint="За замовчуванням показано активні. Дати застосовуються до архіву."
                                buttonTitle="Фільтри переписок"
                                onClear={() => {
                                    setArchivedFrom("");
                                    setArchivedTo("");
                                    if (listMode === "archived") {
                                        showActiveConversations();
                                    }
                                }}
                            >
                                <label className="toolbar-filter-field">
                                    <span>Розділ</span>
                                    <select
                                        value={listMode}
                                        onChange={(e) => handleListSectionChange(e.target.value)}
                                        disabled={loading}
                                    >
                                        <option value="active">Активні</option>
                                        <option value="archived">Архівні</option>
                                    </select>
                                </label>
                                <DateRangeFields
                                    from={archivedFrom}
                                    to={archivedTo}
                                    onFromChange={setArchivedFrom}
                                    onToChange={setArchivedTo}
                                    fromLabel="Архів від"
                                    toLabel="Архів до"
                                />
                            </FilterPopover>
                            <ToolbarSearch
                                value={searchQuery}
                                onChange={setSearchQuery}
                                placeholder="Пошук по темі або повідомленню"
                                title="Пошук переписок"
                            />
                            <button
                                type="button"
                                className={`add-btn refresh toolbar-icon-btn ${showUnreadOnly ? "is-active" : ""}`}
                                onClick={(e) => {
                                    e.currentTarget.blur();
                                    setShowUnreadOnly((prev) => !prev);
                                }}
                                onPointerUp={(e) => e.currentTarget.blur()}
                                title={showUnreadOnly ? "Показати всі" : "Тільки непрочитані"}
                                aria-label={showUnreadOnly ? "Показати всі" : "Тільки непрочитані"}
                                aria-pressed={showUnreadOnly}
                            >
                                {showUnreadOnly ? <MailOpen size={16} /> : <Mail size={16} />}
                            </button>
                        </div>
                    </div>

                    <div className="chat-selector">
                        {showArchiveWarning && !showCreateDropdown && (
                            <div className="archive-warning-banner">
                                <span>ℹ️ Переписки без активності архівуються через {CHAT_ARCHIVE_DAYS} днів</span>
                                <button onClick={() => setShowArchiveWarning(false)}>✕</button>
                            </div>
                        )}

                        {showCreateDropdown && (
                            <div className="chat-create-picker" ref={dropdownRef}>
                                <div className="chat-create-picker__title">Кому хочете написати</div>
                                <div className="chat-create-picker__grid">
                                    <button type="button" className="chat-create-option" onClick={() => startNewChat("client")}>
                                        <span className="chat-create-option__icon">👤</span>
                                        <span className="chat-create-option__text">
                                            <strong>Мешканець</strong>
                                            <small>Оберіть локацію та квартиру адресата</small>
                                        </span>
                                    </button>
                                    <button type="button" className="chat-create-option" onClick={() => startNewChat("location")}>
                                        <span className="chat-create-option__icon">📍</span>
                                        <span className="chat-create-option__text">
                                            <strong>Локація</strong>
                                            <small>Переписка для конкретного будинку або адреси</small>
                                        </span>
                                    </button>
                                    <button type="button" className="chat-create-option" onClick={() => startNewChat("network")}>
                                        <span className="chat-create-option__icon">🏢</span>
                                        <span className="chat-create-option__text">
                                            <strong>Все ОСББ</strong>
                                            <small>Створити переписку по всій мережі</small>
                                        </span>
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className="chat-list-scroll" ref={chatListScrollRef} onWheelCapture={handleChatListWheel}>
                            <div className="chat-list">
                                {displayedConversations.length === 0 && (
                                    <p className="no-messages">
                                        {viewMode === "archived"
                                            ? `Немає архівних переписок${(archivedFrom || archivedTo) ? " для вибраного періоду" : ""}`
                                            : showUnreadOnly
                                                ? "Немає непрочитаних переписок"
                                                : "Немає активних переписок"}
                                    </p>
                                )}
                                {displayedConversations.map((conv) => (
                                    <div
                                        key={conv.id}
                                        className={`chat-item ${highlightedChats.has(conv.id) ? "highlighted" : ""} ${conv.unread_count > 0 ? "has-unread" : ""}`}
                                        onClick={() => {
                                            openConversation(conv);
                                            setHighlightedChats(prev => {
                                                const next = new Set(prev);
                                                next.delete(conv.id);
                                                return next;
                                            });
                                        }}
                                    >
                                        <div className="chat-item-title">
                                            <strong>{conv.title || "Без теми"}</strong>
                                            <span className="chat-mode">
                                                {conv.work_mode === "client" && "👤"}
                                                {conv.work_mode === "location" && "🚪"}
                                                {conv.work_mode === "network" && "🏢"}
                                            </span>
                                        </div>
                                        <div className="chat-item-meta">
                                            <span className="chat-date">
                                                🕒{" "}
                                                {conv.last_message
                                                    ? formatChatDateTime(conv.last_message.created_at)
                                                    : "Без повідомлень"}
                                            </span>
                                            {conv.unread_count > 0 && (
                                                <span className="unread-badge">{conv.unread_count}</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {error && <p className="error">{error}</p>}
                    </div>
                </>
            )}

            {/* ======== Створення нової переписки ======== */}
            {viewMode === "new" && (
                <div className="chat-window">
                    <div className="chat-create">
                        <div className="chat-create-mode-summary">
                            <span>Кому пишемо</span>
                            <button type="button" className="chat-create-mode-chip" onClick={returnToCreatePicker}>
                                Змінити тип
                            </button>
                        </div>
                        <h5>
                            {createMode === "client" && "👤 З мешканцем"}
                            {createMode === "location" && "📍 Локація"}
                            {createMode === "network" && "🏢 Все ОСББ"}
                        </h5>

                        {/* Вибір мешканця */}
                        {createMode === "client" && (
                            <div className="chat-select-client">
                                <label>📍 Локація:</label>
                                <select onChange={(e) => setSelectedResidentLocationId(e.target.value)} value={selectedResidentLocationId}>
                                    <option value="">— виберіть —</option>
                                    {residentLocations.map((loc) => (
                                        <option key={loc.id} value={loc.id}>
                                            {[loc.network_name, [loc.street, loc.house_number, loc.location_name].filter(Boolean).join(" ")].filter(Boolean).join(" • ")}
                                        </option>
                                    ))}
                                </select>

                                <label>🏢 Квартира:</label>
                                <select onChange={(e) => setSelectedApartment(e.target.value)} value={selectedApartment}>
                                    <option value="">— виберіть —</option>
                                    {apartments.map((a) => (
                                        <option key={`${selectedResidentLocationId}-${a.apartment}`} value={a.apartment}>
                                            {a.apartment}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Вибір локації */}
                        {createMode === "location" && (
                            <div className="chat-select-location">
                                <label>📍 Локація:</label>
                                <select onChange={(e) => setSelectedLocation(e.target.value)} value={selectedLocation}>
                                    <option value="">— виберіть —</option>
                                    {locations.map((loc) => (
                                        <option key={loc.id} value={loc.id}>
                                            {[loc.street, loc.house_number, loc.location_name].filter(Boolean).join(" ")}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Для network не потрібен додатковий вибір */}

                        <label>📝 Тема переписки:</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Введіть тему..."
                        />
                        <button onClick={createNewConversation} disabled={!title.trim() || loading}>
                            ➤ Створити
                        </button>
                    </div>

                    {error && <p className="error">{error}</p>}
                </div>
            )}

            {/* ======== Вікно активної переписки ======== */}
            {viewMode === "chat" && activeConversation && (
                <div className="chat-window" ref={chatWindowRef}>
                    <div className="chat-body">
                        <div className="chat-messages" ref={chatMessagesRef}>
                            {messages.length === 0 && <p className="no-messages">Немає повідомлень</p>}
                            {messages.map((msg) => (
                                <div
                                    key={msg.id}
                                    className={`chat-msg ${msg.sender_id === operatorId ? "mine" : "theirs"}`}
                                >
                                    {msg.display_name && msg.sender_id !== operatorId && (
                                        <div className="sender-name">
                                            👤 {msg.display_name}
                                        </div>
                                    )}
                                    <div className="msg-text">{msg.text}</div>
                                    {msg.attachments &&
                                        msg.attachments.map((att) => {
                                            const filename = getAttachmentFilename(att?.file_url);
                                            const externalUrl = isHttpUrl(att?.file_url) ? String(att?.file_url).trim() : "";
                                            const objectUrl = filename ? attachmentObjectUrls[filename] : "";
                                            const previewUrl = externalUrl || objectUrl;

                                            if (att?.file_type && String(att.file_type).startsWith("image/")) {
                                                if (previewUrl) {
                                                    return (
                                                        <a
                                                            key={att.id}
                                                            href={previewUrl}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            title="Відкрити зображення"
                                                        >
                                                            <img src={previewUrl} alt={att.file_name} className="msg-image" />
                                                        </a>
                                                    );
                                                }

                                                return (
                                                    <button
                                                        key={att.id}
                                                        onClick={() => {
                                                            if (filename) downloadAttachment(filename);
                                                        }}
                                                        className="msg-file-btn"
                                                        title="Натисніть для скачування"
                                                    >
                                                        📎 {att.file_name}
                                                    </button>
                                                );
                                            }

                                            return (
                                                <button
                                                    key={att.id}
                                                    onClick={() => {
                                                        if (externalUrl) {
                                                            window.open(externalUrl, "_blank", "noopener,noreferrer");
                                                            return;
                                                        }
                                                        if (filename) downloadAttachment(filename);
                                                    }}
                                                    className="msg-file-btn"
                                                    title="Натисніть для скачування"
                                                >
                                                    📎 {att.file_name}
                                                </button>
                                            );
                                        })
                                    }

                                    <div className="msg-time">
                                        {formatChatTime(msg.created_at)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {(showScrollToTop || showScrollToBottom) && (
                        <div className="chat-scroll-nav" aria-label="Навігація по переписці">
                            {showScrollToTop && (
                                <button
                                    type="button"
                                    className="chat-scroll-nav__btn"
                                    onClick={scrollToTop}
                                    title="На початок"
                                    aria-label="Перейти на початок переписки"
                                >
                                    ⬆
                                </button>
                            )}
                            {showScrollToBottom && (
                                <button
                                    type="button"
                                    className="chat-scroll-nav__btn"
                                    onClick={scrollToBottom}
                                    title="В кінець"
                                    aria-label="Перейти в кінець переписки"
                                >
                                    ⬇
                                </button>
                            )}
                        </div>
                    )}

                    {/* Інпут тільки для активних переписок */}
                    {canComposeInActiveConversation && (
                        <div className="chat-input chat-input-shell">
                            {showAttachmentInfo && (
                                <div className="attachment-info-box">
                                    <button
                                        className="close-info-btn"
                                        onClick={() => setShowAttachmentInfo(false)}
                                    >
                                        ✕
                                    </button>
                                    <div className="attachment-info-content">
                                        <strong>📎 Інформація про прикріплення файлів</strong>
                                        <ul>
                                            <li>📊 Максимум файлів: 3</li>
                                            <li>📁 Дозволені формати: фото (JPG, PNG, GIF, WebP) + PDF</li>
                                            <li>📈 Максимальний розмір: 50MB</li>
                                            <li>⏰ Зберігання на сервері: 90 днів</li>
                                        </ul>
                                    </div>
                                    <input
                                        type="file"
                                        id="file-upload-modal"
                                        ref={fileInputRef}
                                        hidden
                                        onChange={(e) => {
                                            const selectedFile = e.target.files[0];
                                            const validation = validateFileType(selectedFile);
                                            if (!validation.isValid) {
                                                setError(validation.error);
                                                return;
                                            }
                                            setFile(selectedFile);
                                            setError("");
                                            setShowAttachmentInfo(false);
                                        }}
                                    />
                                    <button
                                        className="upload-file-btn"
                                        onClick={() => document.getElementById("file-upload-modal").click()}
                                    >
                                        Вибрати файл
                                    </button>
                                </div>
                            )}
                            <div className="input-wrapper">
                                <textarea
                                    ref={textareaRef}
                                    value={text}
                                    onChange={handleTextareaChange}
                                    placeholder="Введіть повідомлення..."
                                    rows={1}
                                />
                                <input
                                    type="file"
                                    id="file-upload"
                                    ref={fileInputRef}
                                    hidden
                                    onChange={(e) => {
                                        const selectedFile = e.target.files[0];
                                        const validation = validateFileType(selectedFile);
                                        if (!validation.isValid) {
                                            setError(validation.error);
                                            return;
                                        }
                                        setFile(selectedFile);
                                        setError("");
                                    }}
                                />
                                <label
                                    htmlFor="file-upload"
                                    className="attach-btn"
                                    title="Прикріпити файл"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setShowAttachmentInfo(!showAttachmentInfo);
                                    }}
                                >
                                    📎
                                </label>
                                {file && (
                                    <div className="file-preview">
                                        <span>📄 {file.name}</span>
                                        <button
                                            onClick={() => {
                                                setFile(null);
                                                if (fileInputRef.current) {
                                                    fileInputRef.current.value = "";
                                                }
                                            }}
                                        >
                                            ✖
                                        </button>
                                    </div>
                                )}
                            </div>
                            {(text.trim() || file) && (
                                <button className="send-btn" onClick={handleSend} disabled={loading}>
                                    ➤
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ChatOperators;
